<template>
  <div>
    <v-dialog :value="dialog" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Nueva conciliación bancaria</span>
          <v-btn icon small @click="dialog_help = true" class="ml-2" title="Ayuda">
            <v-icon color="grey darken-1">help_outline</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text ref="content">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="money_account"
                :items="money_accounts"
                item-text="name"
                return-object
                auto-select-first
                label="Cuenta a conciliar*"
                prepend-inner-icon="account_balance"
                hide-details="auto"
                autofocus
                @change="onAccountChange"
              >
                <template v-slot:item="{ item }">
                  <div>
                    <div>{{ item.name }}</div>
                    <div class="caption grey--text">{{ item.bank ? item.bank.name : "Sin banco" }}{{ item.cbu ? " · CBU " + item.cbu : "" }}</div>
                  </div>
                </template>
              </v-autocomplete>
              <div class="caption grey--text text--darken-1 mt-1">
                Solo se listan cuentas de tesorería de tipo <b>Banco</b>.
                <template v-if="money_account">
                  <span v-if="account_status && account_status.statement_parser_name">
                    Formato de extracto esperado: <b>{{ account_status.statement_parser_name }}</b>.
                  </span>
                  <span v-else-if="account_status" class="orange--text text--darken-2">
                    La cuenta no tiene formato de extracto configurado. Configuralo en Tesorería → Cuentas.
                  </span>
                </template>
              </div>
              <v-alert v-if="draft" text dense type="warning" class="mt-3 mb-0">
                <div class="d-flex align-center flex-wrap">
                  <div class="flex-grow-1">
                    Ya hay una conciliación en borrador para esta cuenta (período {{ formatUtils.formatDateString(draft.date_from) }} –
                    {{ formatUtils.formatDateString(draft.date_to) }}<template v-if="draft.progress"> · {{ draft.progress.conciliados }} de {{ draft.progress.total }} movimientos conciliados</template>).
                    Guardado el {{ formatUtils.formatDateTimeString(draft.last_saved_at) }}{{ draft.user ? " por " + draft.user : "" }}.
                    Retomala o descartala para cargar un nuevo extracto.
                  </div>
                  <div class="ml-auto">
                    <v-btn v-if="delete_access" text color="error" :loading="loading_discard" @click="dialog_discard = true">Descartar</v-btn>
                    <v-btn color="primary" class="ml-2" @click="goTo(draft.id)">Retomar borrador</v-btn>
                  </div>
                </div>
              </v-alert>
            </v-col>

            <v-col v-if="!draft" cols="12" md="6">
              <DatePickerEsp label="Período desde*" name="date_from" :date="date_from" :isRequired="true" @setDate="(d) => { date_from = d; runPreview(); }" />
            </v-col>
            <v-col v-if="!draft" cols="12" md="6">
              <DatePickerEsp label="Período hasta*" name="date_to" :date="date_to" :isRequired="true" @setDate="(d) => { date_to = d; runPreview(); }" />
            </v-col>
          </v-row>

          <!-- Archivo -->
          <div
            v-if="!draft"
            class="drop-zone pa-5 mt-2"
            :class="{ 'drop-zone--over': drag_over }"
            @dragover.prevent="drag_over = true"
            @dragleave.prevent="drag_over = false"
            @drop.prevent="onDrop"
            @click="!file && $refs.file_input.click()"
          >
            <input ref="file_input" type="file" accept=".xlsx,.xls,.csv" style="display: none" @change="onFileInput" />
            <div v-if="!file" class="text-center grey--text text--darken-1">
              <v-icon size="40" color="grey">upload_file</v-icon>
              <div class="mt-2">Arrastrá el Excel de movimientos del banco o hacé clic para seleccionarlo (.xlsx, .xls, .csv)</div>
            </div>
            <div v-else class="d-flex align-center flex-wrap">
              <v-icon size="44" color="green darken-2" class="mr-4">description</v-icon>
              <div class="flex-grow-1">
                <div class="subtitle-1 font-weight-medium">{{ file.name }}</div>
                <div class="caption grey--text text--darken-1">
                  {{ fileSize }}
                  <template v-if="analysis && analysis.info">
                    · {{ analysis.info.lines_to_import }} movimientos a importar ·
                    {{ formatUtils.formatDateString(analysis.info.file_date_from) }} al {{ formatUtils.formatDateString(analysis.info.file_date_to) }}
                  </template>
                </div>
              </div>
              <v-progress-circular v-if="loading_preview" indeterminate size="22" color="primary" class="mr-4"></v-progress-circular>
              <template v-else-if="analysis">
                <v-chip v-if="formatoValido" small color="green lighten-5" text-color="green darken-3" class="mr-4">
                  <v-icon small left>check_circle</v-icon> Formato válido
                </v-chip>
                <v-chip v-else small color="red lighten-5" text-color="red darken-3" class="mr-4">
                  <v-icon small left>error</v-icon> No se puede procesar
                </v-chip>
              </template>
              <v-btn text color="primary" @click.stop="$refs.file_input.click()"><v-icon left>swap_horiz</v-icon> Cambiar archivo</v-btn>
            </div>
          </div>

          <!-- Errores -->
          <template v-if="analysis && !draft">
            <v-alert v-for="(error, idx) in analysis.errors" :key="'e' + idx" type="error" text border="left" class="mt-4">
              <div class="d-flex align-center flex-wrap">
                <div class="flex-grow-1">
                  <div class="font-weight-bold">{{ errorTitle(error) }}</div>
                  <div>{{ error.msg }}</div>
                  <div v-if="error.code === 'duplicado' && analysis.duplicate">
                    Conciliación {{ analysis.duplicate.label }} · {{ formatUtils.formatDateString(analysis.duplicate.date_from) }} –
                    {{ formatUtils.formatDateString(analysis.duplicate.date_to) }}
                    (cerrada el {{ formatUtils.formatDateTimeString(analysis.duplicate.closed_at) }}).
                  </div>
                  <ul v-if="error.details" class="caption mt-1">
                    <li v-for="(d, i) in error.details" :key="i">{{ d }}</li>
                  </ul>
                </div>
                <v-btn v-if="error.code === 'duplicado' && analysis.duplicate" outlined small color="error" @click="goTo(analysis.duplicate.id)">
                  Ver conciliación
                </v-btn>
                <v-btn v-if="error.code === 'superposicion' && error.conciliacion_id" outlined small color="error" @click="goTo(error.conciliacion_id)">
                  Ver conciliación
                </v-btn>
              </div>
            </v-alert>

            <v-alert v-if="analysis.errors.length === 0 && analysis.warnings.length" type="warning" text border="left" class="mt-4">
              <div class="d-flex align-center flex-wrap">
                <div class="flex-grow-1">
                  <div class="font-weight-bold">Revisá antes de procesar</div>
                  <ul class="mt-1">
                    <li v-for="(w, idx) in analysis.warnings" :key="'w' + idx">{{ w.msg }}</li>
                  </ul>
                </div>
                <template v-if="!warnings_confirmed">
                  <v-btn text color="warning darken-2" @click="focusDates">Corregir</v-btn>
                  <v-btn outlined small color="warning darken-2" class="ml-2" @click="warnings_confirmed = true">Continuar igual</v-btn>
                </template>
                <v-chip v-else small color="warning" text-color="white">Confirmado</v-chip>
              </div>
            </v-alert>

            <v-card v-if="analysis.info && analysis.errors.length === 0" outlined class="mt-4 pa-3">
              <v-row dense class="caption">
                <v-col cols="6" md="3"><div class="grey--text">Formato</div><div class="body-2">{{ analysis.info.parser }}</div></v-col>
                <v-col cols="6" md="3"><div class="grey--text">Saldo inicial del período</div><div class="body-2">{{ formatUtils.formatMoney(analysis.info.saldo_inicial) }}</div></v-col>
                <v-col cols="6" md="3"><div class="grey--text">Saldo final del período</div><div class="body-2">{{ formatUtils.formatMoney(analysis.info.saldo_final) }}</div></v-col>
                <v-col cols="6" md="3"><div class="grey--text">Movimientos en el archivo</div><div class="body-2">{{ analysis.info.lines_total }}</div></v-col>
              </v-row>
            </v-card>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="close">Cerrar</v-btn>
          <v-btn v-if="!draft" text color="success" :disabled="!canProcess" :loading="loading_upload" @click="process">Procesar extracto</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <DialogYesNo
        :open="dialog_discard"
        title="Descartar borrador"
        :message="draft ? 'Se eliminará el borrador ' + draft.label + ' con todo lo conciliado hasta ahora. Los movimientos de tesorería creados desde la conciliación no se eliminan.' : ''"
        nameCancel="Cancelar"
        nameAccept="Descartar"
        @accept="discardDraft"
        @cancel="dialog_discard = false"
      />
    <ModalHelp :show="dialog_help" @close="dialog_help = false" />
  </div>
</template>

<script>
import Constant from "@/constants/sections";
import { conciliacionBancariaService } from "@/libs/ws/conciliacionBancariaService";
import { fileUtils } from "@/libs/utils/fileUtils";
import { formatUtils } from "@/libs/utils/formatUtils";
import RolMixin from "@/mixins/RolMixin.vue";
import DatePickerEsp from "@/components/DatePickerEsp.vue";
import DialogYesNo from "@/components/DialogYesNo.vue";
import ModalHelp from "./ModalHelp.vue";

const ERROR_TITLES = {
  formato: "El formato del archivo no coincide con el banco de la cuenta",
  formato_cuenta: "La cuenta no tiene formato de extracto",
  duplicado: "Este archivo ya fue conciliado",
  integridad: "El extracto tiene saldos inconsistentes",
  superposicion: "El período ya está conciliado",
  vacio: "No hay movimientos para conciliar",
  periodo: "Período inválido",
  archivo: "No se pudo leer el archivo",
  cuenta: "Cuenta inválida",
};

export default {
  components: { DatePickerEsp, DialogYesNo, ModalHelp },
  mixins: [RolMixin],
  props: {
    dialog: Boolean,
    money_accounts: { type: Array, default: () => [] },
  },
  data() {
    return {
      formatUtils: formatUtils,
      section: Constant.SECTION_CONCILIACIONES_BANCARIAS,
      money_account: null,
      account_status: null,
      date_from: null,
      date_to: null,
      file: null,
      file_b64: null,
      drag_over: false,
      analysis: null,
      loading_preview: false,
      loading_upload: false,
      loading_discard: false,
      warnings_confirmed: false,
      dialog_discard: false,
      dialog_help: false,
      preview_seq: 0,
    };
  },
  computed: {
    draft() {
      if (this.analysis && this.analysis.draft) return this.analysis.draft;
      return this.account_status ? this.account_status.draft : null;
    },
    formatoValido() {
      return this.analysis && this.analysis.errors.length === 0;
    },
    fileSize() {
      if (!this.file) return "";
      return this.file.size > 1024 * 1024 ? (this.file.size / 1024 / 1024).toFixed(1) + " MB" : Math.ceil(this.file.size / 1024) + " KB";
    },
    canProcess() {
      return (
        this.formatoValido &&
        !this.loading_preview &&
        !this.draft &&
        (this.analysis.warnings.length === 0 || this.warnings_confirmed)
      );
    },
  },
  watch: {
    // Cada apertura arranca un alta limpia
    dialog(val) {
      if (!val) return;
      Object.assign(this.$data, this.$options.data.call(this));
      this.getAccessArray(); // el reset también limpia los permisos de RolMixin
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    goTo(id) {
      this.close();
      this.$router.push("/" + this.section + "/" + id);
    },
    errorTitle(error) {
      return ERROR_TITLES[error.code] || "Error";
    },
    onAccountChange() {
      this.account_status = null;
      if (!this.money_account) return;
      conciliacionBancariaService.accountStatus({ money_account_id: this.money_account.id }).then((r) => {
        if (!r.status) return;
        this.account_status = r.result;
        // Continuidad: el período arranca al día siguiente del cierre anterior y termina a fin de ese mes
        if (r.result.suggested_date_from) {
          this.date_from = r.result.suggested_date_from;
          const [y, m] = r.result.suggested_date_from.split("-").map(Number);
          this.date_to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
        }
        this.runPreview();
      });
    },
    focusDates() {
      this.warnings_confirmed = false;
      const content = this.$refs.content;
      (content.$el || content).scrollTo({ top: 0, behavior: "smooth" });
    },
    onDrop(e) {
      this.drag_over = false;
      if (e.dataTransfer.files.length) this.setFile(e.dataTransfer.files[0]);
    },
    onFileInput(e) {
      if (e.target.files.length) this.setFile(e.target.files[0]);
      e.target.value = "";
    },
    setFile(file) {
      this.file = file;
      this.file_b64 = null;
      fileUtils.toBase64(file).then((b64) => {
        this.file_b64 = b64;
        this.runPreview();
      });
    },
    requestParams() {
      return {
        money_account_id: this.money_account.id,
        date_from: this.date_from,
        date_to: this.date_to,
        file_name: this.file.name,
        b64_file: this.file_b64,
      };
    },
    runPreview() {
      this.warnings_confirmed = false;
      if (!this.money_account || !this.date_from || !this.date_to || !this.file_b64) return;
      const seq = ++this.preview_seq;
      this.loading_preview = true;
      conciliacionBancariaService
        .preview(this.requestParams())
        .then((r) => {
          if (seq !== this.preview_seq) return;
          if (r.result && r.result.errors) this.analysis = r.result;
          else this.$toast.error(r.msg || r.message || "No se pudo validar el archivo.", "Error");
        })
        .finally(() => {
          if (seq === this.preview_seq) this.loading_preview = false;
        });
    },
    process() {
      this.loading_upload = true;
      conciliacionBancariaService
        .upload({ ...this.requestParams(), confirm_warnings: this.warnings_confirmed })
        .then((r) => {
          if (r.status) {
            this.$toast.success(r.msg, "OK");
            this.goTo(r.result.id);
          } else {
            if (r.result && r.result.errors) this.analysis = r.result;
            this.$toast.error(r.msg || r.message, "Error");
          }
        })
        .finally(() => { this.loading_upload = false; });
    },
    discardDraft() {
      this.dialog_discard = false;
      this.loading_discard = true;
      conciliacionBancariaService
        .remove({ id: this.draft.id })
        .then((r) => {
          if (r.status) {
            this.$toast.success("Borrador descartado.", "OK");
            if (this.analysis) this.analysis.draft = null;
            this.$emit("discarded");
            this.onAccountChange();
          } else {
            this.$toast.error(r.msg || r.message, "Error");
          }
        })
        .finally(() => { this.loading_discard = false; });
    },
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #90caf9;
  border-radius: 6px;
  background: #f7fbff;
  cursor: pointer;
  transition: background 0.15s;
}
.drop-zone--over {
  background: #e3f2fd;
}
</style>
